import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  Tooltip,
  IconButton,
  Tabs,
  Tab
} from '@mui/material'
import {
  Security,
  Search,
  ExpandMore,
  Person,
  TableChart,
  Timeline,
  TrendingUp,
  Assessment,
  Refresh,
  Assignment,
  AdminPanelSettings,
  Visibility
} from '@mui/icons-material'
import axios from 'axios'
import VerificacionObjetivos from './VerificacionObjetivos'
import AdminObjetivos from './AdminObjetivos'
import TestComponent from './TestComponent'

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auditoria-tabpanel-${index}`}
      aria-labelledby={`auditoria-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  )
}

const AuditoriaViewer = () => {
  const navigate = useNavigate()
  const { user, isAdmin } = useAuth()
  const [tabValue, setTabValue] = useState(0)
  const [logs, setLogs] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [adminDialogOpen, setAdminDialogOpen] = useState(false)
  const [filters, setFilters] = useState({
    tabla: '',
    accion: '',
    usuario_id: '',
    limit: 50,
    offset: 0
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1
  })

  useEffect(() => {
    loadData()
    loadStats()
  }, [filters, user])

  const loadData = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const params = new URLSearchParams()
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value)
      })
      
      const response = await axios.get(`/api/auditoria?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setLogs(response.data.logs || [])
      setPagination({
        page: Math.floor(filters.offset / filters.limit) + 1,
        totalPages: Math.ceil((response.data.pagination?.total || 0) / filters.limit)
      })
    } catch (error) {
      setError('Error al cargar logs: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get('/api/auditoria/stats', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setStats(response.data)
    } catch (error) {
      console.error('Error al cargar estadísticas:', error)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
      offset: 0 // Reset pagination
    })
  }

  const handlePageChange = (event, page) => {
    setFilters({
      ...filters,
      offset: (page - 1) * filters.limit
    })
  }

  const getAccionColor = (accion) => {
    switch (accion) {
      case 'CREATE': return 'success'
      case 'UPDATE': return 'warning'
      case 'DELETE': return 'error'
      default: return 'default'
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString()
  }

  const renderChanges = (changes) => {
    if (!changes) return 'N/A'
    
    try {
      const changesObj = typeof changes === 'string' ? JSON.parse(changes) : changes
      return (
        <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
          <pre style={{ 
            fontSize: '0.75rem', 
            margin: 0, 
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace'
          }}>
            {JSON.stringify(changesObj, null, 2)}
          </pre>
        </Box>
      )
    } catch {
      return changes.toString()
    }
  }

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      log.tabla?.toLowerCase().includes(searchLower) ||
      log.accion?.toLowerCase().includes(searchLower) ||
      log.usuario_nombre?.toLowerCase().includes(searchLower) ||
      log.ip_address?.toLowerCase().includes(searchLower)
    )
  })

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cargando auditoría...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Security sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Auditoría del Sistema
          </Typography>
        </Box>
        <IconButton onClick={() => { loadData(); loadStats(); }}>
          <Refresh />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Pestañas */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab 
            icon={<Timeline />} 
            label="Logs de Auditoría" 
            id="auditoria-tab-0"
            aria-controls="auditoria-tabpanel-0"
          />
          <Tab 
            icon={<Assignment />} 
            label="Verificación de Objetivos" 
            id="auditoria-tab-1"
            aria-controls="auditoria-tabpanel-1"
          />
          {isAdmin && (
            <Tab
              icon={<AdminPanelSettings />}
              label="Administrar Objetivos"
              id="auditoria-tab-2"
              aria-controls="auditoria-tabpanel-2"
            />
          )}
        </Tabs>
      </Box>

      {/* Panel de Logs de Auditoría */}
      <TabPanel value={tabValue} index={0}>
        {/* Estadísticas */}
        {stats && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assessment />
                <Typography variant="h6">Estadísticas ({stats.period})</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {/* Estadísticas generales */}
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Timeline color="primary" />
                        <Typography variant="h6">Actividad General</Typography>
                      </Box>
                      <Typography variant="body2">Total eventos: {stats.general_stats.total_events}</Typography>
                      <Typography variant="body2">Tablas monitoreadas: {stats.general_stats.tables_monitored}</Typography>
                      <Typography variant="body2">Usuarios activos: {stats.general_stats.active_users}</Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip label={`Creaciones: ${stats.general_stats.creates}`} color="success" size="small" sx={{ mr: 0.5 }} />
                        <Chip label={`Actualizaciones: ${stats.general_stats.updates}`} color="warning" size="small" sx={{ mr: 0.5 }} />
                        <Chip label={`Eliminaciones: ${stats.general_stats.deletes}`} color="error" size="small" />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Top usuarios */}
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Person color="primary" />
                        <Typography variant="h6">Usuarios Más Activos</Typography>
                      </Box>
                      <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                        {stats.top_users.slice(0, 5).map((user, index) => (
                          <Box key={index} sx={{ mb: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {user.nombre}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.total_actions} acciones
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Estadísticas por tabla */}
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <TableChart color="primary" />
                        <Typography variant="h6">Actividad por Tabla</Typography>
                      </Box>
                      <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                        {stats.table_stats.map((table, index) => (
                          <Box key={index} sx={{ mb: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {table.tabla}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {table.total_actions} acciones
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Filtros */}
        <Paper sx={{ mb: 2, p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Buscar en logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Tabla</InputLabel>
                <Select
                  value={filters.tabla}
                  label="Tabla"
                  onChange={(e) => handleFilterChange('tabla', e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="procedimientos">Procedimientos</MenuItem>
                  <MenuItem value="gestiones">Gestiones</MenuItem>
                  <MenuItem value="usuarios">Usuarios</MenuItem>
                  <MenuItem value="configuracion">Configuración</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Acción</InputLabel>
                <Select
                  value={filters.accion}
                  label="Acción"
                  onChange={(e) => handleFilterChange('accion', e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="CREATE">Crear</MenuItem>
                  <MenuItem value="UPDATE">Actualizar</MenuItem>
                  <MenuItem value="DELETE">Eliminar</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Límite</InputLabel>
                <Select
                  value={filters.limit}
                  label="Límite"
                  onChange={(e) => handleFilterChange('limit', e.target.value)}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabla de logs */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fecha/Hora</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Tabla</TableCell>
                <TableCell>Acción</TableCell>
                <TableCell>Registro ID</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Cambios</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(log.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {log.usuario?.nombre || 'Sistema'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={log.tabla} 
                      color="primary" 
                      variant="outlined" 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.accion}
                      color={getAccionColor(log.accion)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      #{log.registro_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={log.user_agent || 'N/A'}>
                      <Typography variant="body2" color="text.secondary">
                        {log.ip_address}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ maxWidth: 300 }}>
                      {renderChanges(log.cambios)}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver detalles">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/auditoria/${log.id}`)}
                        color="primary"
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filteredLogs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No se encontraron logs de auditoría
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginación */}
        {pagination.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </TabPanel>

      {/* Panel de Verificación de Objetivos */}
      <TabPanel value={tabValue} index={1}>
        <VerificacionObjetivos />
      </TabPanel>

      {/* Panel de Administración de Objetivos */}
      {isAdmin && (
        <TabPanel value={tabValue} index={2}>
          <AdminObjetivos onClose={() => setTabValue(1)} />
        </TabPanel>
      )}
    </Box>
  )
}

export default AuditoriaViewer